import type Phaser from 'phaser';
import {
  N1_ACTIONS,
  tierFrameMs,
  tierFrameNames,
  tierOfLabel,
  type N1ActionId,
  type N1Manifest,
  type N1TierLabel,
} from '../assets/n1Manifest';

export interface RegisteredAction {
  id: N1ActionId;
  frames: number;
  frameRate: number;
  loop: boolean;
  missingFrames: string[];
}

/**
 * Create one Phaser animation per N1 action from the loaded tier texture.
 * Animation key = action id. Frame rate = the manifest's review timing for the tier
 * (gameplay compresses via anims.globalTimeScale or per-sprite timeScale).
 * Returns what was registered plus any frame the texture is missing — the caller fails loudly on those.
 */
export function registerN1Animations(
  scene: Phaser.Scene,
  manifest: N1Manifest,
  textureKey: string,
  label: N1TierLabel,
): RegisteredAction[] {
  const { tier } = tierOfLabel(label);
  const texture = scene.textures.get(textureKey);
  const out: RegisteredAction[] = [];
  for (const id of N1_ACTIONS) {
    const names = tierFrameNames(manifest, id, tier);
    const missing = names.filter((n) => !texture.has(n));
    const loop = manifest.actions[id].loop;
    const frameRate = 1000 / tierFrameMs(manifest, id, tier);
    if (missing.length === 0) {
      if (scene.anims.exists(id)) scene.anims.remove(id);
      scene.anims.create({
        key: id,
        frames: names.map((frame) => ({ key: textureKey, frame })),
        frameRate,
        repeat: loop ? -1 : 0,
      });
    }
    out.push({ id, frames: names.length, frameRate, loop, missingFrames: missing });
  }
  return out;
}
